import React, { useEffect, useState } from "react";
import defaultFront from "../../assets/default-front.jpg";
import defaultBack from "../../assets/default-back.jpg";
import API_URL from "../../services/api";
import "./Books.css";

const Books = () => {
  const [books, setBooks] = useState([]);
  const [search, setSearch] = useState("");
  const [genre, setGenre] = useState("All");
  const [language, setLanguage] = useState("All");
  const [selectedBook, setSelectedBook] = useState(null);
  const [loading, setLoading] = useState(true);

  // ==========================================
  // FETCH BOOKS
  // ==========================================

  const fetchBooks = async () => {
    try {
      setLoading(true);

      const res = await fetch(`${API_URL}/api/books`);

      if (!res.ok) {
        throw new Error("Failed to fetch books");
      }

      const data = await res.json();

      setBooks(data);
    } catch (err) {
      console.log("BOOK FETCH ERROR:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBooks();
  }, []);

  // ==========================================
  // DYNAMIC GENRES
  // ==========================================

  const genres = [
    "All",
    ...new Set(
      books
        .map((book) => book.genre)
        .filter(
          (value) =>
            value &&
            value.trim() !== ""
        )
    ),
  ];

  // ==========================================
  // DYNAMIC LANGUAGES
  // ==========================================

  const languages = [
    "All",
    ...new Set(
      books
        .map((book) => book.language)
        .filter(
          (value) =>
            value &&
            value.trim() !== ""
        )
    ),
  ];

  // ==========================================
  // FILTER BOOKS
  // ==========================================

  const filteredBooks = books
    // Only books available in library
    .filter(
      (book) =>
        Number(book.is_for_sale) !== 1
    )

    .filter((book) => {
      const searchText =
        search.toLowerCase().trim();

      const matchSearch =
        !searchText ||
        book.title
          ?.toLowerCase()
          .includes(searchText) ||
        book.author
          ?.toLowerCase()
          .includes(searchText) ||
        book.genre
          ?.toLowerCase()
          .includes(searchText);

      const matchGenre =
        genre === "All" ||
        book.genre === genre;

      const matchLanguage =
        language === "All" ||
        book.language === language;

      return (
        matchSearch &&
        matchGenre &&
        matchLanguage
      );
    });

  // ==========================================
  // CLEAR FILTERS
  // ==========================================

  const clearFilters = () => {
    setSearch("");
    setGenre("All");
    setLanguage("All");
  };

  const hasFilters =
    search.trim() !== "" ||
    genre !== "All" ||
    language !== "All";

  // ==========================================
  // CLOSE MODAL
  // ==========================================

  const closeModal = () => {
    setSelectedBook(null);
  };

  // ==========================================
  // DATE / TEXT HELPERS
  // ==========================================

  const formatYear = (year) => {
    return year || "N/A";
  };

  // ==========================================
  // UI
  // ==========================================

  return (
    <div className="user-books-page">

      {/* ======================================
          HEADER
      ====================================== */}

      <div className="books-header">


        <div>
          <h1>
            Library Books
          </h1>

          <p>
            Browse and explore books available
            in our library collection.
          </p>
        </div>

      </div>

      {/* ======================================
          FILTER SECTION
      ====================================== */}

      <div className="books-filter-box">

        {/* SEARCH */}

        <div className="books-search-wrapper">

          <span className="books-search-icon">
            🔍
          </span>

          <input
            type="text"
            placeholder="Search by title, author or genre..."
            value={search}
            onChange={(e) =>
              setSearch(e.target.value)
            }
            className="books-search"
          />

          {search && (
            <button
              className="books-clear-search"
              onClick={() =>
                setSearch("")
              }
            >
              ✕
            </button>
          )}

        </div>

        {/* GENRE */}

        <select
          value={genre}
          onChange={(e) =>
            setGenre(e.target.value)
          }
          className="books-select"
        >
          <option value="All">
            All Genres
          </option>

          {genres
            .filter(
              (item) => item !== "All"
            )
            .sort()
            .map((item) => (
              <option
                key={item}
                value={item}
              >
                {item}
              </option>
            ))}
        </select>

        {/* LANGUAGE */}

        <select
          value={language}
          onChange={(e) =>
            setLanguage(e.target.value)
          }
          className="books-select"
        >
          <option value="All">
            All Languages
          </option>

          {languages
            .filter(
              (item) => item !== "All"
            )
            .sort()
            .map((item) => (
              <option
                key={item}
                value={item}
              >
                {item}
              </option>
            ))}
        </select>

        {/* CLEAR */}

        {hasFilters && (
          <button
            className="clear-filters-button"
            onClick={clearFilters}
          >
            Clear Filters
          </button>
        )}

      </div>

      {/* ======================================
          BOOK COUNT
      ====================================== */}

      {!loading && (
        <div className="books-result-bar">

          <h3>
            📖{" "}
            {filteredBooks.length}{" "}
            Library Book
            {filteredBooks.length !== 1
              ? "s"
              : ""}
          </h3>

          {hasFilters && (
            <span>
              Filtered results
            </span>
          )}

        </div>
      )}

      {/* ======================================
          LOADING
      ====================================== */}

      {loading && (
        <div className="books-empty">

          <div className="books-empty-icon">
            ⏳
          </div>

          <h2>
            Loading Books
          </h2>

          <p>
            Please wait while we load
            the library collection.
          </p>

        </div>
      )}

      {/* ======================================
          EMPTY RESULT
      ====================================== */}

      {!loading &&
        filteredBooks.length === 0 && (
          <div className="books-empty">

            <div className="books-empty-icon">
              {hasFilters
                ? "🔍"
                : "📚"}
            </div>

            <h2>
              {hasFilters
                ? "No Books Found"
                : "No Library Books Available"}
            </h2>

            <p>
              {hasFilters
                ? "Try changing your search or filters."
                : "There are currently no books available in the library collection."}
            </p>

            {hasFilters && (
              <button
                className="empty-clear-button"
                onClick={clearFilters}
              >
                Clear Filters
              </button>
            )}

          </div>
        )}

      {/* ======================================
          BOOK GRID
      ====================================== */}

      {!loading &&
        filteredBooks.length > 0 && (
          <div className="books-grid">

            {filteredBooks.map(
              (book) => (
                <div
                  key={book.book_id}
                  className="book-card"
                  onClick={() =>
                    setSelectedBook(book)
                  }
                >

                  {/* COVER */}

                  <div className="book-cover-wrapper">

                    <img
                      src={
                        book.front_cover
                          ? `${API_URL}/uploads/books/${book.front_cover}`
                          : defaultFront
                      }
                      alt={
                        book.title ||
                        "Book Cover"
                      }
                      className="book-cover"
                    />

                  </div>

                  {/* DATA */}

                  <div className="book-card-content">

                    <h3 className="book-title">
                      {book.title}
                    </h3>

                    <p className="book-author">
                      {book.author ||
                        "Unknown Author"}
                    </p>

                    <div className="book-meta">

                      {book.genre && (
                        <span>
                          {book.genre}
                        </span>
                      )}

                      {book.language && (
                        <span>
                          {book.language}
                        </span>
                      )}

                    </div>

                    <div className="book-availability">
                      📚 Available in Library
                    </div>

                  </div>

                </div>
              )
            )}

          </div>
        )}

      {/* ======================================
          BOOK DETAILS MODAL
      ====================================== */}

      {selectedBook && (
        <div
          className="book-modal-overlay"
          onClick={closeModal}
        >

          <div
            className="book-modal"
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            {/* CLOSE */}

            <button
              className="book-modal-close"
              onClick={closeModal}
              aria-label="Close"
            >
              ✕
            </button>

            {/* TITLE */}

            <div className="modal-header">

              <h2>
                {selectedBook.title}
              </h2>

              <span>
                📚 Library Collection
              </span>

            </div>

            {/* COVERS */}

            <div className="modal-covers">

              <div className="modal-cover-item">

                <h4>
                  Front Cover
                </h4>

                <img
                  src={
                    selectedBook.front_cover
                      ? `${API_URL}/uploads/books/${selectedBook.front_cover}`
                      : defaultFront
                  }
                  alt={
                    selectedBook.title
                  }
                  className="modal-cover"
                />

              </div>

              <div className="modal-cover-item">

                <h4>
                  Back Cover
                </h4>

                <img
                  src={
                    selectedBook.back_cover
                      ? `${API_URL}/uploads/books/${selectedBook.back_cover}`
                      : defaultBack
                  }
                  alt="Back Cover"
                  className="modal-cover"
                />

              </div>

            </div>

            {/* DETAILS */}

            <div className="book-details">

              <div className="detail-row">
                <strong>
                  Author
                </strong>

                <span>
                  {selectedBook.author ||
                    "N/A"}
                </span>
              </div>

              <div className="detail-row">
                <strong>
                  Genre
                </strong>

                <span>
                  {selectedBook.genre ||
                    "N/A"}
                </span>
              </div>

              <div className="detail-row">
                <strong>
                  Language
                </strong>

                <span>
                  {selectedBook.language ||
                    "N/A"}
                </span>
              </div>

              <div className="detail-row">
                <strong>
                  Publisher
                </strong>

                <span>
                  {selectedBook.publisher ||
                    "N/A"}
                </span>
              </div>

              <div className="detail-row">
                <strong>
                  Publication Year
                </strong>

                <span>
                  {formatYear(
                    selectedBook.publication_year
                  )}
                </span>
              </div>

              {selectedBook.isbn && (
                <div className="detail-row">
                  <strong>
                    ISBN
                  </strong>

                  <span>
                    {selectedBook.isbn}
                  </span>
                </div>
              )}

              <div className="detail-description">

                <strong>
                  Description
                </strong>

                <p>
                  {selectedBook.description ||
                    "No description available."}
                </p>

              </div>

            </div>

            {/* AVAILABILITY */}

            <div className="modal-availability">

              <span className="availability-icon">
                ✓
              </span>

              <div>
                <strong>
                  Available in Library
                </strong>

                <p>
                  This book is part of
                  the library collection.
                </p>
              </div>

            </div>

            {/* CLOSE BUTTON */}

            <button
              className="modal-close-button"
              onClick={closeModal}
            >
              Close
            </button>

          </div>

        </div>
      )}

    </div>
  );
};

export default Books;